import type { HistoricalPerformanceMetric } from '@/features/decisions/decisions.api.types';

import { backtestSeriesColor, fmtMetricValue, policyLabel } from './historicalPerformanceFormat';

// ECharts line chart for one metric: one series per experiment over the period
// points. The reference (incumbent) is drawn muted/dashed; comparisons solid.
export function buildMetricChartOption(metric: HistoricalPerformanceMetric | undefined) {
  if (!metric || metric.series.length === 0) {
    return {
      title: { text: 'No data', left: 'center', top: 'middle', textStyle: { color: '#94a3b8', fontSize: 14 } },
      xAxis: { show: false },
      yAxis: { show: false },
    };
  }

  // Union of period labels across series (series share the same window).
  const periods = metric.series[0]?.period.map((p) => p.period) ?? [];

  return {
    tooltip: {
      trigger: 'axis',
      valueFormatter: (v: unknown) => (typeof v === 'number' ? fmtMetricValue(v, metric.unit) : '—'),
    },
    legend: { type: 'scroll', bottom: 0, textStyle: { fontSize: 11, color: '#334155' } },
    grid: { top: 24, right: 24, bottom: 48, left: 64 },
    xAxis: {
      type: 'category',
      data: periods,
      axisLine: { lineStyle: { color: '#cbd5e1' } },
      axisTick: { show: false },
      axisLabel: { fontSize: 10, color: '#64748b' },
    },
    yAxis: {
      type: 'value',
      name: metric.label,
      nameLocation: 'middle',
      nameGap: 48,
      nameTextStyle: { fontSize: 11, fontWeight: 600, color: '#334155' },
      axisLabel: { fontSize: 10, color: '#64748b', formatter: (v: number) => fmtMetricValue(v, metric.unit) },
      splitLine: { lineStyle: { color: '#f1f5f9' } },
    },
    series: (() => {
      // Color by policy (Fully Optimized = darkest green, Quantity Only = lighter,
      // Incumbent = muted) so shades are consistent regardless of series order.
      return metric.series.map((s) => {
        const color = backtestSeriesColor(s.experiment_id, s.is_reference);
        return {
          name: policyLabel(s),
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 5,
          lineStyle: s.is_reference ? { type: 'dashed', width: 2 } : { width: 2 },
          itemStyle: { color },
          data: s.period.map((p) => p.value),
        };
      });
    })(),
  };
}
